import {
  Button,
  evaluate,
  Icon,
  ModalContent,
  Skeleton,
  Tooltip,
  Typography,
} from "@kyan/ui";
import Link from "next/link";

import { LoadingGem } from "#/components/LoadingGem/LoadingGem";
import { routes } from "#/config/routes";
import { tokens } from "#/config/web3/tokens";
import { useGetExplorerUrl } from "#/hooks/useGetExplorerUrl";
import { useUsdcBalance } from "#/hooks/useUsdcBalance";
import { formatValue } from "#/utils/valueFormatters";

import { type TxModalVariant, type TxState } from "../../EquityModal.types";
import { SuccessSummary } from "./components/SuccessSummary";

export interface TxSuccessModalContentProps {
  variant: TxModalVariant;
  onTxHistoryClick?: () => void;
  onTradeClick?: () => void;
  txState: TxState;
}

export const TxSuccessModalContent: React.FC<TxSuccessModalContentProps> = ({
  variant,
  onTxHistoryClick,
  onTradeClick,
  txState: { fromPortfolio, toPortfolio, txHash },
}) => {
  const { usdcBalance, isLoading: isBalanceLoading } = useUsdcBalance();

  const isDeposit = variant === "deposit";
  const isWithdraw = variant === "withdraw";
  const isTransfer = variant === "transfer";

  const baseExplorerURL = useGetExplorerUrl();
  const txLookup = `${baseExplorerURL}${txHash}`;

  return (
    <ModalContent
      shouldScroll={false}
      className="flex max-w-108.25 flex-col items-center justify-between pt-10"
    >
      <div className="flex flex-col items-center gap-2">
        {isDeposit && (
          <Typography
            size={12}
            font="sans"
            spacing="wide"
            weight="medium"
            className="text-brandBlue"
          >
            Deposit successful
          </Typography>
        )}
        <Typography bold grotesk primary size={24}>
          {evaluate({
            "Ready to trade": isDeposit,
            "Withdrawal successful": isWithdraw,
            "Transfer successful": isTransfer,
          })}
        </Typography>
      </div>

      <LoadingGem className="mt-4 h-32" />

      <div className="bg-buttonBg dark:bg-buttonFill my-4 w-full rounded-sm">
        <Typography
          element="p"
          variant="primary"
          className="border-divider flex h-11 w-full items-center border-b px-4"
        >
          Summary
        </Typography>
        <div className="flex w-full flex-col gap-1 px-4 py-3">
          {!isTransfer && (
            <Typography className="my-2 flex items-center justify-between">
              <Tooltip content="Balance on your external wallet, outside of Kyan.">
                <span className="cursor-help underline">USDC Balance</span>
              </Tooltip>
              <span>
                {isBalanceLoading ? (
                  <Skeleton className="h-3" />
                ) : (
                  formatValue(usdcBalance, { decimals: tokens.USDC.decimals })
                )}
              </span>
            </Typography>
          )}

          {isTransfer && fromPortfolio && (
            <SuccessSummary action={variant} market={fromPortfolio} />
          )}
          {toPortfolio && (
            <SuccessSummary action={variant} market={toPortfolio} />
          )}
        </div>
      </div>

      <div className="flex w-full flex-col items-center gap-4">
        <div className="flex w-full items-center gap-4">
          <Link href={routes.portfolio.history}>
            <Button size="lg" variant="secondary" onClick={onTxHistoryClick}>
              Transaction history
            </Button>
          </Link>
          <Button size="lg" className="flex-1" onClick={onTradeClick}>
            {evaluate({
              Trade: isDeposit,
              Close: !isDeposit,
            })}
          </Button>
        </div>

        {isDeposit && baseExplorerURL && (
          <a
            href={txLookup}
            target="_blank"
            rel="noreferrer"
            className="flex cursor-pointer justify-center gap-1"
          >
            <Typography
              grotesk
              primary
              size={12}
              className="text-brandBlue items-center hover:underline"
            >
              View on Arbiscan
            </Typography>
            <Icon
              name="arrow-up"
              className="text-brandBlue rotate-45 pt-1 text-[8px]"
            />
          </a>
        )}
      </div>
    </ModalContent>
  );
};
